/* NeuroLog Pro — main dashboard shell. Tabs across the 6 modules, floating
   WhatsApp ICE button, PIN gate, auto-lock timer, Recovery Mode overlay. */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pill, Lightning, Microphone, ChartBar, Gear, WhatsappLogo, Lock } from "@phosphor-icons/react";
import "./health.css";
import health, { getHealthToken, clearHealthToken } from "./healthApi";
import LockScreen from "./LockScreen";
import MedicationManager from "./MedicationManager";
import SeizureLogger from "./SeizureLogger";
import VoiceMemos from "./VoiceMemos";
import Analytics from "./Analytics";
import Settings from "./Settings";
import FallDetectOverlay from "./FallDetectOverlay";

const TABS = [
  { id: "meds",    label: "Meds",     Icon: Pill },
  { id: "seizure", label: "Log",      Icon: Lightning },
  { id: "voice",   label: "Voice",    Icon: Microphone },
  { id: "trends",  label: "Trends",   Icon: ChartBar },
  { id: "set",     label: "Settings", Icon: Gear },
];

const AUTO_LOCK_MS = 5 * 60 * 1000;   // 5 min

export default function HealthTracker() {
  const [unlocked, setUnlocked] = useState(!!getHealthToken());
  const [tab, setTab] = useState("meds");
  const [recovery, setRecovery] = useState(false);
  const [iceOpen, setIceOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [fallOpen, setFallOpen] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("fallpreview") === "1"
  );
  const lockTimer = useRef(null);

  /* Make /health-tracker installable as its own home-screen app.
     Injects the PWA manifest + iOS meta tags on mount, removes them on
     unmount so the rest of the site keeps its own manifest. */
  useEffect(() => {
    const created = [];
    const inject = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      created.push(el);
    };
    // Point-of-truth manifest for the PWA install prompt
    inject("link", { rel: "manifest", href: "/health-tracker-manifest.json", id: "nl-manifest" });
    // iOS treats these as "add to home screen" hints
    inject("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    inject("meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    inject("meta", { name: "apple-mobile-web-app-title", content: "NeuroLog" });
    inject("link", { rel: "apple-touch-icon", href: "/health-tracker-icon-192.png" });
    // Dark theme colour so the browser chrome (URL bar) matches
    inject("meta", { name: "theme-color", content: "#0B132B", id: "nl-theme-color" });
    const prevTitle = document.title;
    document.title = "NeuroLog Pro";
    return () => {
      created.forEach((el) => el.remove());
      document.title = prevTitle;
    };
  }, []);

  // Load settings once we're unlocked (for the ICE button)
  useEffect(() => {
    if (unlocked) health.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [unlocked]);

  /* Auto-lock on inactivity */
  useEffect(() => {
    if (!unlocked) return;
    const reset = () => {
      clearTimeout(lockTimer.current);
      lockTimer.current = setTimeout(() => {
        clearHealthToken();
        setUnlocked(false);
        toast.info("Locked after inactivity");
      }, AUTO_LOCK_MS);
    };
    ["mousemove", "keydown", "touchstart", "click"].forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(lockTimer.current);
      ["mousemove", "keydown", "touchstart", "click"].forEach((e) => window.removeEventListener(e, reset));
    };
  }, [unlocked]);

  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;

  const openIce = () => {
    // Prefer the new contacts list; fall back to the legacy single-contact
    const contacts = (settings?.emergency_contacts || []).filter((c) => c.phone);
    const legacy = settings?.emergency_whatsapp;
    const list = contacts.length > 0
      ? contacts
      : (legacy ? [{ name: settings?.emergency_contact_name || "", phone: legacy }] : []);
    if (list.length === 0) {
      toast.error("Add at least one emergency contact in Settings first");
      return setTab("set");
    }
    setIceOpen(true);
    let done = false;
    const fireAll = (coords) => {
      if (done) return;
      done = true;
      setIceOpen(false);
      const base = settings.default_distress_message || "MEDICAL EMERGENCY";
      const geo = coords
        ? `\n\nLive location: https://maps.google.com/?q=${coords.latitude},${coords.longitude}\n(±${Math.round(coords.accuracy)}m)`
        : "";
      const msg = encodeURIComponent(base + geo);
      list.forEach((c, i) => {
        const phone = c.phone.replace(/\D+/g, "");
        // Small stagger so popup blockers don't collapse simultaneous window.opens
        setTimeout(() => window.open(`https://wa.me/${phone}?text=${msg}`, "_blank"), i * 350);
      });
      toast.success(`Alert fired to ${list.length} contact${list.length > 1 ? "s" : ""}`);
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fireAll(pos.coords),
        () => fireAll(null),
        { enableHighAccuracy: true, timeout: 4000 },
      );
      setTimeout(() => fireAll(null), 4200);
    } else {
      fireAll(null);
    }
  };

  const manualLock = () => {
    clearHealthToken();
    setUnlocked(false);
    toast.success("Locked");
  };

  return (
    <div
      data-testid="nl-dashboard"
      className={recovery ? "nl-recovery-mode" : ""}
      style={{ background: "var(--nl-bg)", minHeight: "100vh", color: "var(--nl-text)" }}
    >
      <div className="max-w-[720px] mx-auto px-4 pt-6 pb-32">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>NEUROLOG PRO</div>
            <div className="font-display text-xl font-black">Private tracker</div>
          </div>
          <button onClick={manualLock} data-testid="nl-manual-lock"
                  className="h-9 px-3 rounded font-label text-[10px] tracking-widest inline-flex items-center gap-2"
                  style={{ border: "1px solid var(--nl-line)", color: "var(--nl-muted)" }}>
            <Lock size={12} /> LOCK
          </button>
        </header>

        <div data-testid={`nl-tab-panel-${tab}`}>
          {tab === "meds"    && <MedicationManager />}
          {tab === "seizure" && <SeizureLogger onShakeStart={() => setFallOpen(true)} />}
          {tab === "voice"   && <VoiceMemos />}
          {tab === "trends"  && <Analytics />}
          {tab === "set"     && <Settings recovery={recovery} setRecovery={setRecovery} />}
        </div>
      </div>

      {/* Floating WhatsApp ICE button */}
      <button
        data-testid="nl-ice-button"
        onClick={openIce}
        className="fixed z-40 h-16 w-16 rounded-full nl-ice-pulse flex items-center justify-center"
        style={{ bottom: 88, right: 20, background: "var(--nl-danger)" }}
        aria-label="Emergency WhatsApp ICE"
        title="Emergency — send WhatsApp with GPS"
      >
        <WhatsappLogo size={28} weight="fill" color="#fff" />
      </button>

      {iceOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur">
          <div className="rounded-2xl p-6 text-center max-w-xs mx-4"
               style={{ background: "var(--nl-bg-2)", border: "1px solid var(--nl-danger)" }}>
            <div className="font-display text-lg font-black" style={{ color: "var(--nl-danger)" }}>Getting GPS…</div>
            <div className="text-xs mt-2" style={{ color: "var(--nl-muted)" }}>Opening WhatsApp with your location and distress message.</div>
          </div>
        </div>
      )}

      <FallDetectOverlay
        open={fallOpen}
        settings={settings}
        onDismiss={() => setFallOpen(false)}
        onFired={() => setFallOpen(false)}
      />

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 flex" style={{ background: "var(--nl-bg-2)", borderTop: "1px solid var(--nl-line)" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`nl-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="flex-1 py-3 flex flex-col items-center gap-0.5"
            style={{ color: tab === t.id ? "var(--nl-accent)" : "var(--nl-muted)" }}
          >
            <t.Icon size={20} weight={tab === t.id ? "fill" : "regular"} />
            <span className="font-label text-[9px] tracking-widest">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
